import * as React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
    Outlet,
    NavLink,
    Navigate,
    useNavigate,
    useLocation,
    useParams,
} from 'react-router-dom';
import BrandLogo from 'common/components/brand-logo';
import MainHeader from 'layouts/main/main-header';
import { useTranslation } from 'react-i18next';
import { signOut } from 'services/auth.service';
import { getToken, clearAllDataBrowser } from 'utils/helpers';
import { toast } from 'react-toastify';
import { ArrowLeftIcon, ArrowRightIcon } from '@mui/x-date-pickers';
import { colors, Role } from 'common/constants';
import Upgrade from './components/upgrade';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import bgInfomation from 'assets/images/backgrounds/bg-infomation.jpg';
import bgUpdateProfile from 'assets/images/backgrounds/bg-update-profile.jpg';
import bgChangePassword from 'assets/images/backgrounds/bg-change-password.jpg';
import { useAppSelector, useAppDispatch } from 'common/hook';
import { clearUser } from 'slices/userSlice';
import { resetPrimaryColor } from 'slices/themeSlice';
import { RootState } from 'plugins/store';
import { demoUserId } from 'common/constants';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';

const drawerWidth = 285;
const headerHeight = 90;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(8)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(10)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
    showSidebar: boolean;
}
const renderBackgroundColor = (id: string) => {
    const pathname = window.location?.pathname || '';
    switch (pathname) {
        case '/admin/profile':
        case '/admin/account-management/add':
        case `/admin/account-management/${id}/edit`:
        case `/admin/customer/add`:
        case `/admin/customer/${id}/edit`:
        case '/admin/profile/edit':
        case '/admin/change-password':
            return 'unset';
        default:
            return '#EFF1FF';
    }
};
const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open, showSidebar }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: open && showSidebar ? `calc(100% - ${drawerWidth}px)` : '100%',
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
    ...(!open && {
        marginLeft: drawerWidth,
        width: showSidebar ? `calc(100% - ${80}px)` : `100%`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
    backgroundColor: renderBackgroundColor(
        useParams<{ id: string }>().id as string
    ),
    boxShadow: 'none',
}));
const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
    '& .MuiList-root': {
        padding: '1rem',
        paddingRight: open ? '1rem' : 0,
        paddingLeft: open ? '1rem' : 0,
        '& .MuiListItem-root .MuiButtonBase-root': {
            minHeight: 60,
            justifyContent: open ? 'initial' : 'center',
            paddingLeft: 20,
            paddingRight: 20,
            borderRadius: open ? '40px' : '0px',
            color: theme.palette.primary.main,
        },
        '& .MuiListItem-root .active .MuiButtonBase-root': {
            backgroundColor: theme.palette.primary.main,
            color: 'var(--color-label-text)',
            '&:hover': {
                backgroundColor: theme.palette.primary.main,
            },
        },
        '& .MuiListItem-root .MuiButtonBase-root .MuiListItemIcon-root': {
            minWidth: 0,
            justifyContent: 'center',
            color: theme.palette.primary.main,
        },
        '& .MuiListItem-root .active .MuiButtonBase-root .MuiListItemIcon-root':
            {
                color: 'var(--color-label-text)',
            },
    },
}));
interface MainLayoutProps {
    showSidebar: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ showSidebar }) => {
    const userInfo = useAppSelector((state: RootState) => state.user);
    const userRole = React.useMemo(() => userInfo.attributes.role, [userInfo]);
    const userId = React.useMemo(() => userInfo.attributes.id, [userInfo]);
    const id = useParams<{ id: string }>().id;
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(() => {
        const saved = localStorage.getItem('openSidebar');
        return saved !== null ? JSON.parse(saved) : true;
    });

    React.useEffect(() => {
        localStorage.setItem('openSidebar', JSON.stringify(open));
    }, [open]);

    const token = getToken();
    if (!token) {
        return <Navigate to="/login" />;
    }
    const sidebarNavItems = [
        {
            text: t('layout.sidebar.dashboard'),
            pathname: 'overview',
            icon: <DashboardOutlinedIcon sx={{ fontSize: 32 }} />,
            iconActive: <DashboardOutlinedIcon sx={{ fontSize: 32 }} />,
            roles: [Role.CLIENT, Role.SUPER, Role.SALES],
            exceptUserId: [demoUserId],
        },
        {
            text: t('layout.sidebar.campaign'),
            pathname: 'campaign',
            icon: <BarChartOutlinedIcon sx={{ fontSize: 32 }} />,
            iconActive: <BarChartOutlinedIcon sx={{ fontSize: 32 }} />,
            roles: [Role.CLIENT, Role.SUPER, Role.SALES],
            exceptUserId: [],
        },
        {
            text: t('layout.sidebar.payment'),
            pathname: 'payment',
            icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 32 }} />,
            iconActive: (
                <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 32 }} />
            ),
            roles: [Role.CLIENT, Role.SUPER, Role.ACCOUNTANT],
            exceptUserId: [demoUserId],
        },
        {
            text: 'Quản lý tài khoản',
            pathname: 'account-management',
            icon: <ManageAccountsOutlinedIcon sx={{ fontSize: 32 }} />,
            iconActive: <ManageAccountsOutlinedIcon sx={{ fontSize: 32 }} />,
            roles: [Role.SUPER],
            exceptUserId: [demoUserId],
        },
        {
            text: 'Khách hàng',
            pathname: 'customer',
            icon: <GroupOutlinedIcon sx={{ fontSize: 32 }} />,
            iconActive: <GroupOutlinedIcon sx={{ fontSize: 32 }} />,
            roles: [Role.SUPER, Role.SALES],
            exceptUserId: [demoUserId],
        },
        {
            text: 'Cấu hình',
            pathname: 'general-configuration',
            icon: <SettingsOutlinedIcon sx={{ fontSize: 32 }} />,
            iconActive: <SettingsOutlinedIcon sx={{ fontSize: 32 }} />,
            roles: [Role.SUPER],
            exceptUserId: [demoUserId],
        },
        {
            text: 'Quà tặng',
            pathname: 'reward',
            icon: <CardGiftcardIcon sx={{ fontSize: 32 }} />,
            iconActive: <CardGiftcardIcon sx={{ fontSize: 32 }} />,
            roles: [Role.CLIENT, Role.SUPER, Role.SALES],
            exceptUserId: [demoUserId],
        },
    ];

    const sidebarActionItems = [
        {
            text: t('common.logout'),
            icon: <LogoutIcon sx={{ fontSize: 32 }} />,
            action: async () => {
                try {
                    const response = await signOut();
                    if (response.success) {
                        toast.success(response.message);
                        dispatch(clearUser());
                        dispatch(resetPrimaryColor());
                        clearAllDataBrowser();
                        navigate('/login');
                    } else {
                        console.error('Sign out error:', response.message);
                    }
                } catch (error) {
                    console.error('Sign out error:', error);
                }
            },
        },
    ];

    const handleDrawerClose = () => {
        setOpen((open: boolean) => !open);
    };

    const renderBackground = (() => {
        const pathname = location?.pathname || '';
        switch (pathname) {
            case '/admin/profile':
            case '/admin/account-management/add':
            case `/admin/account-management/${id}/edit`:
            case '/admin/customer/add':
            case `/admin/customer/${id}/edit`:
                return bgInfomation;
            case '/admin/profile/edit':
                return bgUpdateProfile;
            case '/admin/change-password':
                return bgChangePassword;
            default:
                return '';
        }
    })();
    return (
        <Box sx={{ display: 'flex', position: 'relative' }}>
            <CssBaseline />
            <AppBar position="fixed" open={open} showSidebar={showSidebar}>
                <Toolbar
                    sx={{
                        padding: '16px 52px 16px 52px !important',
                    }}
                >
                    <MainHeader showSidebar={showSidebar} />
                </Toolbar>
            </AppBar>

            {showSidebar && (
                <>
                    <Box
                        sx={{
                            position: 'fixed',
                            left: open ? drawerWidth : 80,
                            top: '50%',
                            zIndex: 999,
                            borderRadius: '0 10px 10px 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px',
                            width: '24px',
                            height: '50px',
                            backgroundColor: 'white',
                            color: colors.main,
                            transition: theme.transitions.create(['left'], {
                                easing: theme.transitions.easing.sharp,
                                duration:
                                    theme.transitions.duration.enteringScreen,
                            }),
                            cursor: 'pointer',
                        }}
                        onClick={handleDrawerClose}
                    >
                        {!open ? <ArrowRightIcon /> : <ArrowLeftIcon />}
                    </Box>
                    <Drawer
                        variant="permanent"
                        open={open}
                        sx={{
                            ' .MuiDrawer-paper': {
                                border: 'none',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                marginTop: '32px',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            {open ? (
                                <BrandLogo
                                    variant="full"
                                    width={145}
                                    height={105}
                                    onClick={() => navigate('/')}
                                />
                            ) : (
                                <BrandLogo
                                    variant="noText"
                                    width={65}
                                    height={65}
                                    onClick={() => navigate('/')}
                                />
                            )}
                        </Box>
                        <List sx={{ marginTop: '32px' }}>
                            {sidebarNavItems.map((item) => {
                                if (!item.roles.includes(userRole as Role)) {
                                    return null;
                                }
                                if (item.exceptUserId.includes(userId)) {
                                    return null;
                                }
                                return (
                                    <ListItem
                                        key={item.text}
                                        disablePadding
                                        sx={{ display: 'block' }}
                                    >
                                        <NavLink
                                            to={item.pathname}
                                            className={({
                                                isActive,
                                                isPending,
                                                isTransitioning,
                                            }) =>
                                                [
                                                    isPending ? 'pending' : '',
                                                    isActive ? 'active' : '',
                                                    isTransitioning
                                                        ? 'transitioning'
                                                        : '',
                                                ].join(' ')
                                            }
                                            style={{
                                                textDecoration: 'none',
                                            }}
                                        >
                                            {({ isActive }) => (
                                                <ListItemButton
                                                    sx={{
                                                        minHeight: '60px',
                                                        marginBottom: '8px',
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            width: '40px',
                                                            height: '40px',
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'center',
                                                        }}
                                                    >
                                                        {isActive
                                                            ? item.iconActive
                                                            : item.icon}
                                                    </ListItemIcon>
                                                    {open && (
                                                        <ListItemText
                                                            primary={item.text}
                                                            sx={{
                                                                opacity: open
                                                                    ? 1
                                                                    : 0,
                                                                marginLeft:
                                                                    '8px',
                                                                fontSize:
                                                                    '18px',
                                                            }}
                                                        />
                                                    )}
                                                </ListItemButton>
                                            )}
                                        </NavLink>
                                    </ListItem>
                                );
                            })}
                            {sidebarActionItems.map((item) => (
                                <ListItem
                                    key={item.text}
                                    disablePadding
                                    sx={{ display: 'block' }}
                                >
                                    <ListItemButton onClick={item.action}>
                                        <ListItemIcon
                                            sx={{
                                                width: '40px',
                                                height: '40px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        {open && (
                                            <ListItemText
                                                primary={item.text}
                                                sx={{
                                                    opacity: open ? 1 : 0,
                                                    marginLeft: '8px',
                                                    fontSize: '18px',
                                                }}
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            ))}
                            {userId !== demoUserId &&
                                userRole === 'client' &&
                                open && <Upgrade openSidebar={open} />}
                        </List>
                    </Drawer>
                </>
            )}

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 5,
                    backgroundColor: theme.palette.secondary.main,
                    backgroundImage: `url(${renderBackground})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'bottom',
                    backgroundSize: 'cover',
                    minHeight: '100vh',
                }}
            >
                <DrawerHeader
                    sx={{
                        height: headerHeight,
                    }}
                />
                <Outlet />
            </Box>
        </Box>
    );
};

export default MainLayout;
